import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isEmpty, merge } from 'lodash';
import { invoiceAPI } from 'redux/invoiceAPI';
import { PayerError } from '@payer-admin-ui/components';
import { emptyInvoiceFilters, InvoiceFilters } from 'views/Pages/invoices/invoicesGlobalState';
import {
  emptyIAFilters,
  InvoiceAggregationsFilters,
} from 'views/Pages/invoiceAggregations/invoiceAggregationsGlobalState';
import { bankFilePaymentsAPI } from '../../../../../../redux/bankFilePaymentsAPI';
import { invoiceAggregationAPI } from '../../../../../../redux/invoiceAggregationAPI';
import { MappingTargetType } from '../../../../../../utils/constants';

type FetchInvoiceByInvoiceNumberParams = {
  invoiceNumber: number;
};

type FetchInvoiceBySearchParams = {
  searchTerm: string;
};

type FetchInvoiceAggregationByIdParams = {
  invoiceAggregationId: string;
};

type MapToInvoicesParams = {
  bankFilePaymentId: number;
  bankFilePaymentAmount: number;
  bankFilePaymentMappings: any[];
  creditInvoiceIdsAndAmounts: Array<{ invoiceId: string; amount: string }>;
  debitInvoiceIdsAndAmounts: Array<{ invoiceId: string; amount: string }>;
};

type MapToIasParams = {
  bankFilePaymentId: number;
  debitInvoiceAggregationIdsAndAmounts: Array<{ invoiceAggregationId: string; amount: string }>;
};

type AddOrUpdateWantedMappingPayload = {
  bankFilePaymentId: number;
  targetId: string;
  mappingTargetId?: string;
  amountToMap: string | null;
  targetType: MappingTargetType;
};

type ResetPayload = {
  bankFilePaymentId: number;
};

type ModalState = {
  isFetching?: boolean | null;
  success?: boolean | null;
  error?: any;
  mappingOperation?: {
    isFetching: boolean;
    success: boolean | null;
    error: any;
  };
  wantedMappings?: Record<
    string,
    {
      amount: string | null;
      type: MappingTargetType;
      mappingTargetId?: string;
    }
  >;
  bankFilePayment?: any;
};

type SplitMapPayinDialogState = {
  invoices: Record<
    string,
    {
      isFetching: boolean;
      invoice?: any;
      error?: any;
    }
  >;
  invoiceAggregations: Record<
    string,
    {
      isFetching: boolean;
      invoiceAggregation?: any;
      error?: any;
    }
  >;
  foundMappingTarget: {
    isFetching: boolean;
    invoice?: any;
    invoiceAggregation?: any;
    error?: any;
  };
  modals: Record<string, ModalState>;
};

export const fetchInvoiceByInvoiceNumber = createAsyncThunk(
  'bankFilePayments/actions/fetchInvoiceByInvoiceNumber',
  async ({ invoiceNumber }: FetchInvoiceByInvoiceNumberParams) => {
    if (!invoiceNumber) {
      return null;
    }
    return await invoiceAPI.fetchByInvoiceNumber({ invoiceNumber });
  },
);

export const fetchFirstMatchingInvoiceBySearch = createAsyncThunk(
  'bankFilePayments/actions/fetchFirstMatchingInvoiceBySearch',
  async ({ searchTerm }: FetchInvoiceBySearchParams) => {
    if (isEmpty(searchTerm)) {
      return null;
    }
    const page = 0;
    const pageSize = 1;
    const filters: InvoiceFilters = {
      ...emptyInvoiceFilters,
      search: searchTerm,
    };
    const response = await invoiceAPI.fetchPage({ page, pageSize, filters, sort: [] });
    const invoices = response.contents;
    if (isEmpty(invoices)) {
      throw new PayerError({ code: 'invoiceOrInvoiceStatementNotFound' });
    }
    return invoices[0];
  },
);

export const fetchInvoiceAggregationById = createAsyncThunk(
  'bankFilePayments/actions/fetchInvoiceAggregationById',
  async ({ invoiceAggregationId }: FetchInvoiceAggregationByIdParams) => {
    if (isEmpty(invoiceAggregationId)) {
      return null;
    }
    return await invoiceAggregationAPI.fetchById(invoiceAggregationId);
  },
);

export const fetchFirstMatchingInvoiceAggregationBySearch = createAsyncThunk(
  'bankFilePayments/actions/fetchFirstMatchingInvoiceAggregationBySearch',
  async ({ searchTerm }: FetchInvoiceBySearchParams) => {
    if (isEmpty(searchTerm)) {
      return null;
    }
    const page = 0;
    const pageSize = 1;
    const filters: InvoiceAggregationsFilters = {
      ...emptyIAFilters,
      search: searchTerm,
    };
    // First search with the paged view
    const response = await invoiceAggregationAPI.fetchPage({ page, pageSize, appliedFilters: filters, sort: [] });
    const ias = response.contents;
    if (isEmpty(ias)) {
      throw new PayerError({ code: 'invoiceOrInvoiceStatementNotFound' });
    }
    const firstIa = ias[0];
    const invoiceAggregationId = String(firstIa.invoiceAggregationNumber);
    // We need to fetch the IA by id to get balance and paymentStatus
    return await invoiceAggregationAPI.fetchById(invoiceAggregationId);
  },
);

export const mapToInvoices = createAsyncThunk(
  'bankFilePayments/actions/mapToInvoices',
  async ({
    bankFilePaymentId,
    bankFilePaymentAmount,
    bankFilePaymentMappings,
    creditInvoiceIdsAndAmounts,
    debitInvoiceIdsAndAmounts,
  }: MapToInvoicesParams) => {
    return bankFilePaymentsAPI.bankFilePayments.mapToInvoices({
      bankFilePaymentId,
      bankFilePaymentAmount,
      bankFilePaymentMappings,
      creditInvoiceIdsAndAmounts,
      debitInvoiceIdsAndAmounts,
    });
  },
);

export const mapToIas = createAsyncThunk(
  'bankFilePayments/actions/mapToIas',
  async ({ bankFilePaymentId, debitInvoiceAggregationIdsAndAmounts }: MapToIasParams) => {
    return bankFilePaymentsAPI.bankFilePayments.mapToIas({
      bankFilePaymentId,
      debitInvoiceAggregationIdsAndAmounts,
    });
  },
);

const initialModalState: Partial<ModalState> = {
  isFetching: false,
  success: null,
  error: null,
};

const splitMapPayinModalSlice = createSlice({
  name: 'bankFilePayments/actions/SplitMapPayinDialog',
  initialState: {
    invoices: {},
    invoiceAggregations: {},
    foundMappingTarget: { isFetching: false },
    modals: {},
  } as SplitMapPayinDialogState,
  reducers: {
    addOrUpdateWantedMapping: (
      state,
      {
        payload: { bankFilePaymentId, targetId, mappingTargetId, amountToMap, targetType },
      }: { payload: AddOrUpdateWantedMappingPayload },
    ) => {
      merge(state.modals, {
        [bankFilePaymentId]: {
          wantedMappings: { [targetId]: { amount: amountToMap, type: targetType, mappingTargetId } },
        },
      });
    },
    reset: (state, { payload: { bankFilePaymentId } }: { payload: ResetPayload }) => {
      state.modals[bankFilePaymentId] = { isFetching: null, success: null, error: null };
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchInvoiceByInvoiceNumber.pending, (state, { meta: { arg } }) => {
      merge(state.invoices, { [arg.invoiceNumber]: { isFetching: true, error: null } });
      state.foundMappingTarget = { isFetching: true };
    });
    builder.addCase(fetchInvoiceByInvoiceNumber.fulfilled, (state, { meta: { arg }, payload: invoice }) => {
      state.invoices[arg.invoiceNumber] = { isFetching: false, invoice };
      state.foundMappingTarget = { isFetching: false, invoice };
    });
    builder.addCase(fetchInvoiceByInvoiceNumber.rejected, (state, { meta: { arg }, error }) => {
      state.invoices[arg.invoiceNumber] = { isFetching: false, error };
      state.foundMappingTarget = { isFetching: false, error };
    });

    builder.addCase(fetchFirstMatchingInvoiceBySearch.pending, state => {
      state.foundMappingTarget = { isFetching: true };
    });
    builder.addCase(fetchFirstMatchingInvoiceBySearch.fulfilled, (state, { payload: invoice }) => {
      if (invoice) {
        state.invoices[invoice.invoiceNumber] = { isFetching: false, invoice };
        state.foundMappingTarget = { isFetching: false, invoice };
      }
    });
    builder.addCase(fetchFirstMatchingInvoiceBySearch.rejected, (state, { error }) => {
      state.foundMappingTarget = { isFetching: false, error };
    });

    builder.addCase(fetchInvoiceAggregationById.pending, (state, { meta: { arg } }) => {
      merge(state.invoiceAggregations, { [arg.invoiceAggregationId]: { isFetching: true, error: null } });
      state.foundMappingTarget = { isFetching: true };
    });
    builder.addCase(fetchInvoiceAggregationById.fulfilled, (state, { meta: { arg }, payload: invoiceAggregation }) => {
      state.invoiceAggregations[arg.invoiceAggregationId] = { isFetching: false, invoiceAggregation };
      state.foundMappingTarget = { isFetching: false, invoiceAggregation };
    });
    builder.addCase(fetchInvoiceAggregationById.rejected, (state, { meta: { arg }, error }) => {
      state.invoiceAggregations[arg.invoiceAggregationId] = { isFetching: false, error };
      state.foundMappingTarget = { isFetching: false, error };
    });

    builder.addCase(fetchFirstMatchingInvoiceAggregationBySearch.pending, state => {
      state.foundMappingTarget = { isFetching: true };
    });
    builder.addCase(
      fetchFirstMatchingInvoiceAggregationBySearch.fulfilled,
      (state, { payload: invoiceAggregation }) => {
        if (invoiceAggregation) {
          state.invoiceAggregations[invoiceAggregation.invoiceStatement.invoiceAggregationNumber] = {
            isFetching: false,
            invoiceAggregation,
          };
          state.foundMappingTarget = { isFetching: false, invoiceAggregation };
        }
      },
    );
    builder.addCase(fetchFirstMatchingInvoiceAggregationBySearch.rejected, (state, { error }) => {
      state.foundMappingTarget = { isFetching: false, error };
    });

    builder.addCase(mapToInvoices.pending, (state, { meta: { arg } }) => {
      state.modals[arg.bankFilePaymentId] = { ...initialModalState };
      merge(state.modals, {
        [arg.bankFilePaymentId]: { mappingOperation: { isFetching: true, success: null, error: null } },
      });
    });
    builder.addCase(
      mapToInvoices.fulfilled,
      (state, { meta: { arg }, payload: { bankFilePayment, performedMappings } }) => {
        // Store the updated BankFilePayment. We don't use this right now, but we could perhaps use it to update the row
        // in BankFilePaymentsPage somehow, to get immediately updated state after the modal has closed.
        merge(state.modals, { [arg.bankFilePaymentId]: { bankFilePayment } });

        // Update invoices
        performedMappings.forEach((performedMapping: any) => {
          const invoice = performedMapping.invoice;
          merge(state.invoices, { [invoice.id]: { isFetching: false, invoice } });
        });

        // Signal that we succeeded, and also close the modal
        merge(state.modals, {
          [arg.bankFilePaymentId]: { mappingOperation: { isFetching: false, success: true, error: null } },
        });
        // Clear the list of wanted mappings
        if (state.modals[arg.bankFilePaymentId]) {
          state.modals[arg.bankFilePaymentId].wantedMappings = {};
        }
      },
    );
    builder.addCase(mapToInvoices.rejected, (state, { meta: { arg }, error }) => {
      merge(state.modals, {
        [arg.bankFilePaymentId]: { mappingOperation: { isFetching: false, success: false, error } },
      });
    });

    builder.addCase(mapToIas.pending, (state, { meta: { arg } }) => {
      state.modals[arg.bankFilePaymentId] = { ...initialModalState };
      merge(state.modals, {
        [arg.bankFilePaymentId]: { mappingOperation: { isFetching: true, success: null, error: null } },
      });
    });
    builder.addCase(mapToIas.fulfilled, (state, { meta: { arg } }) => {
      // Signal that we succeeded, and also close the modal
      merge(state.modals, {
        [arg.bankFilePaymentId]: { mappingOperation: { isFetching: false, success: true, error: null } },
      });
      // Clear the list of wanted mappings
      if (state.modals[arg.bankFilePaymentId]) {
        state.modals[arg.bankFilePaymentId].wantedMappings = {};
      }
    });
    builder.addCase(mapToIas.rejected, (state, { meta: { arg }, error }) => {
      merge(state.modals, {
        [arg.bankFilePaymentId]: { mappingOperation: { isFetching: false, success: false, error } },
      });
    });
  },
});

export const {
  actions: { addOrUpdateWantedMapping, reset },
  reducer: bankFilePaymentActionSplitMapPayinModalReducer,
} = splitMapPayinModalSlice;
